// NPM packages
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// All other imports
import { MembershipTier } from 'types/SignUpData';

export interface Feature {
  label: string;
  tiers: MembershipTier[];
  isNew?: boolean;
}

export const FEATURES: Feature[] = [
  { label: 'Download data from the repository', tiers: ['Tier 1', 'Tier 2', 'Tier 3'] },
  { label: 'Use the CLI', tiers: ['Tier 1', 'Tier 2', 'Tier 3'] },
  { label: 'Request project membership', tiers: ['Tier 1', 'Tier 2', 'Tier 3'] },
  { label: 'Launch educational or model training notebooks', tiers: ['Tier 2', 'Tier 3'], isNew: true },
  { label: 'Utilize dataset notebook feature', tiers: ['Tier 3'], isNew: true },
];

export interface TierInfo {
  value: MembershipTier;
  title: string;
  subtitle?: string;
  note?: string;
}

export const TIERS: TierInfo[] = [
  { value: 'Tier 1', title: 'Tier 1' },
  { value: 'Tier 2', title: 'Tier 2' },
  { value: 'Tier 3', title: 'Tier 3', note: 'Requires a valid ORCID ID.' },
];

export const TIER_ORDER: MembershipTier[] = ['Tier 1', 'Tier 2', 'Tier 3'];

/** Returns the theme color for a given tier, reusing the existing palette. */
export const useTierColor = (tier: MembershipTier): string => {
  const theme = useTheme();
  switch (tier) {
    case 'Tier 1':
      return theme.palette.primary.main;
    case 'Tier 2':
      return theme.palette.secondary.light;
    case 'Tier 3':
      return theme.palette.primary.dark;
    default:
      return theme.palette.primary.main;
  }
};

interface TierCardProps {
  tier: TierInfo;
  /** The logged-in user's current tier — used for the "Current Tier" badge and NEW flags. */
  userCurrentTier?: MembershipTier;
  /** Whether this card is the currently selected option (sign-up or upgrade). */
  isSelected?: boolean;
  /** Called when the card or its button is clicked. Omit for read-only cards. */
  onSelect?: (tier: MembershipTier) => void;
  /** Button label when not selected. Defaults to "SELECT". */
  selectLabel?: string;
  /** Button label when selected. Defaults to "SELECTED". */
  selectedLabel?: string;
  /** Whether to show the select button. Set false for display-only cards. */
  showSelectButton?: boolean;
  /** Whether to apply the colored border highlight when this card is the current tier. Defaults to true. */
  highlightCurrentTier?: boolean;
}

const TierCard: React.FC<TierCardProps> = ({
  tier,
  userCurrentTier,
  isSelected = false,
  onSelect,
  selectLabel = 'SELECT',
  selectedLabel = 'SELECTED',
  showSelectButton = true,
  highlightCurrentTier = true,
}) => {
  const theme = useTheme();
  const tierColor = useTierColor(tier.value);
  const isCurrentTier = userCurrentTier === tier.value;
  const clickable = Boolean(onSelect);

  const includedFeatures = FEATURES.filter((f) => f.tiers.includes(tier.value));
  const excludedFeatures = FEATURES.filter((f) => !f.tiers.includes(tier.value));

  const handleClick = (): void => {
    if (onSelect) onSelect(tier.value);
  };

  return (
    <Card
      variant="outlined"
      onClick={clickable ? handleClick : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        border: (isCurrentTier && highlightCurrentTier)
            ? `2px solid ${tierColor}`
            : isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : `2px solid ${theme.palette.grey[300]}`,
        boxShadow: isSelected ? `0 4px 20px ${tierColor}33` : undefined,
        '&:hover': clickable ? { boxShadow: `0 4px 16px ${tierColor}22` } : undefined,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          bgcolor: tierColor,
          color: '#fff',
          pt: 2.5,
          pb: 4,
          textAlign: 'center',
          clipPath: 'polygon(0 0, 100% 0, 100% 75%, 50% 100%, 0 75%)',
        }}
      >
        <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
          {tier.title}
        </Typography>
        {tier.subtitle && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {tier.subtitle}
          </Typography>
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 2 }}>
        {includedFeatures.length > 0 && (
          <>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontWeight: 'bold',
                mb: 0.5,
              }}
            >
              Included
            </Typography>
            {includedFeatures.map((feature) => {
              const isNewForUser =
                feature.isNew &&
                userCurrentTier &&
                !feature.tiers.includes(userCurrentTier);
              return (
                <Box
                  key={feature.label}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
                >
                  <CheckCircleIcon
                    sx={{ color: 'success.main', fontSize: 20, mt: '2px', flexShrink: 0 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.primary', fontWeight: isNewForUser ? 'bold' : 'normal' }}
                  >
                    {feature.label}
                  </Typography>
                </Box>
              );
            })}
          </>
        )}
        {excludedFeatures.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontWeight: 'bold',
                mb: 0.5,
              }}
            >
              Not included in this tier
            </Typography>
            {excludedFeatures.map((feature) => (
              <Box
                key={feature.label}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
              >
                <CancelIcon
                  sx={{ color: 'error.main', fontSize: 20, mt: '2px', flexShrink: 0 }}
                />
                <Typography
                  variant="body1"
                >
                  {feature.label}
                </Typography>
              </Box>
            ))}
          </>
        )}
        {tier.note && (
            <Box
                sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 1.5,
                p: 1,
                bgcolor: `${tierColor}14`,
                borderRadius: 1,
                }}
            >
            <WarningAmberIcon
                fontSize="small"
                sx={{ color: 'warning.main', flexShrink: 0 }}
                aria-hidden
            />
            <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontStyle: 'italic' }}
            >
              {tier.note}
            </Typography>
        </Box>
)}
      </CardContent>

      {(showSelectButton && onSelect || isCurrentTier) && (
        <CardActions sx={{ justifyContent: 'center', alignItems: 'center', gap: 1, pb: 2, flexWrap: 'wrap' }}>
            {isCurrentTier && (
                <Chip
                    label="Current Tier"
                    size="small"
                    sx={{
                        bgcolor: '#fff',
                        color: tierColor,
                        border: `1px solid ${tierColor}`,
                        fontWeight: 'bold',
                    }}
                />
            )}
            {showSelectButton && onSelect && (
                <Button
                variant={isSelected ? 'contained' : 'outlined'}
                color="primary"
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                }}
                sx={{
                    fontWeight: 'bold',
                    px: 4,
                    ...(isSelected
                    ? {}
                    : {
                        borderColor: tierColor,
                        color: tierColor,
                        '&:hover': {
                            bgcolor: `${tierColor}14`,
                            borderColor: tierColor,
                        },
                        }),
                }}
                >
                {isSelected ? selectedLabel : selectLabel}
                </Button>
            )}
        </CardActions>
      )}
    </Card>
  );
};

export default TierCard;